/**
 * Zod schemas for the compliance / drift detection API surface.
 *
 * Mirrors `opus/api-shim/compliance/types.py`. ComplianceResult is loose
 * intentionally — `current` and `desired` can be any JSON value.
 */
import { z } from "zod";

/* ============================================ */
/* PRESETS                                      */
/* ============================================ */

const CompliancePresetSchema = z.object({
  slug:        z.string(),
  description: z.string(),
  tags:        z.array(z.string()).default([]),
  /** True when a concrete check module is wired for this slug. */
  supported:   z.boolean(),
  /** True when the check module also exposes a remediate() entry point —
   *  i.e. the React /compliance page should show the "Remediate" button.
   *  Defaults to false for backwards-compat with old API responses. */
  remediable:  z.boolean().default(false),
  /** Which vendor's API this preset compares against. Drives the
   *  picker: Meraki presets get a Meraki network dropdown, Aruba
   *  presets get a tenant placeholder (single literal target id). */
  vendor:      z.enum(["meraki", "aruba"]).default("meraki"),
});
export type CompliancePreset = z.infer<typeof CompliancePresetSchema>;
export const CompliancePresetListSchema = z.array(CompliancePresetSchema);

/* ============================================ */
/* SCAN REPORT                                  */
/* ============================================ */

const ComplianceResultSchema = z.object({
  pointer:  z.string(),
  current:  z.unknown(),
  desired:  z.unknown(),
  status:   z.enum(["compliant", "non-compliant", "ignore"]),
  errors:   z.array(z.string()).default([]),
  warnings: z.array(z.string()).default([]),
});
export type ComplianceResult = z.infer<typeof ComplianceResultSchema>;

const ComplianceSummarySchema = z.object({
  compliant:        z.number().default(0),
  "non-compliant":  z.number().default(0),
  ignore:           z.number().default(0),
});
export type ComplianceSummary = z.infer<typeof ComplianceSummarySchema>;

export const DiagnosisResultSchema = z.object({
  process:    z.string(),
  presetSlug: z.string(),
  target:     z.string(),
  summary:    ComplianceSummarySchema,
  results:    z.array(ComplianceResultSchema),
  /** Server-issued id when the scan was persisted. Optional because some
   *  legacy code paths returned the result without persisting. */
  scanId:     z.string().optional(),
  durationMs: z.number().optional(),
  /** User-applied labels — populated by /api/compliance/scans/{id} reload
   *  + PATCH. Empty for fresh scans until the operator labels them. */
  tags:       z.array(z.string()).default([]),
});
export type DiagnosisResult = z.infer<typeof DiagnosisResultSchema>;

/* ============================================ */
/* SCHEDULED PROBES                             */
/* ============================================ */

/** One row from the compliance_probes table — drives the admin section
 *  of /compliance. Probes fire on the housekeeping tick (default 6h). */
const ComplianceProbeSchema = z.object({
  id:          z.string(),
  kind:        z.string(),
  presetSlug:  z.string(),
  networkId:   z.string(),
  vendor:      z.enum(["meraki", "aruba"]),
  enabled:     z.boolean(),
  createdAt:   z.string(),
  createdBy:   z.string(),
});
export type ComplianceProbe = z.infer<typeof ComplianceProbeSchema>;
export const ComplianceProbeListSchema = z.array(ComplianceProbeSchema);

/* ============================================ */
/* SCAN HISTORY                                 */
/* ============================================ */

/** Summary row for /api/compliance/scans/ — drives the recent-scans rail.
 *  Doesn't carry the full per-pointer results blob; click a row to fetch
 *  the full report via /api/compliance/scans/{id}. */
const ComplianceScanSummarySchema = z.object({
  id:          z.string(),
  ranAt:       z.string(),
  ranBy:       z.string(),
  networkId:   z.string(),
  networkName: z.string().nullable().optional(),
  presetSlug:  z.string(),
  processName: z.string(),
  summary:     ComplianceSummarySchema,
  durationMs:  z.number().nullable().optional(),
  source:      z.enum(["manual", "agent", "scheduled_probe"]),
  /** Which vendor's API this scan ran against. Defaults to 'meraki'
   *  for back-compat with rows persisted before the column was added. */
  vendor:      z.enum(["meraki", "aruba"]).default("meraki"),
  /** User-applied labels (baseline, before-fix, after-fix, ...). Defaults
   *  to empty for back-compat with rows persisted before the column. */
  tags:        z.array(z.string()).default([]),
});
export type ComplianceScanSummary = z.infer<typeof ComplianceScanSummarySchema>;
export const ComplianceScanSummaryListSchema = z.array(ComplianceScanSummarySchema);

/** Full saved scan row — includes the per-pointer results so the React
 *  drill-in can replay without re-running upstream. */
export const ComplianceScanFullSchema = ComplianceScanSummarySchema.extend({
  results: z.array(ComplianceResultSchema),
});
export type ComplianceScanFull = z.infer<typeof ComplianceScanFullSchema>;

/* ============================================ */
/* POINTER HISTORY                              */
/* ============================================ */

/** One row in a JSON-pointer's per-scan timeline. Used to trace when
 *  a specific config field drifted from baseline across the last N
 *  scans for a (network, preset) pair. */
const PointerHistoryEntrySchema = z.object({
  scanId:   z.string(),
  ranAt:    z.string(),
  ranBy:    z.string(),
  source:   z.string(),
  tags:     z.array(z.string()).default([]),
  status:   z.enum(["compliant", "non-compliant", "ignore"]),
  current:  z.unknown(),
  desired:  z.unknown(),
  errors:   z.array(z.string()).default([]),
});
export type PointerHistoryEntry = z.infer<typeof PointerHistoryEntrySchema>;

export const PointerHistorySchema = z.object({
  networkId:   z.string(),
  presetSlug:  z.string(),
  pointer:     z.string(),
  totalFound:  z.number(),
  history:     z.array(PointerHistoryEntrySchema),
});
export type PointerHistory = z.infer<typeof PointerHistorySchema>;

/* ============================================ */
/* SCAN DIFF                                    */
/* ============================================ */

/** Header row for either side of a diff — same fields the rail uses. */
const ScanDiffHeadSchema = z.object({
  id:          z.string(),
  ranAt:       z.string(),
  ranBy:       z.string(),
  networkId:   z.string(),
  networkName: z.string().nullable().optional(),
  presetSlug:  z.string(),
  processName: z.string(),
  summary:     ComplianceSummarySchema,
  durationMs:  z.number().nullable().optional(),
  source:      z.string(),
});

/** One bucket entry — a pointer that landed in fixed / regressed / persistent.
 *  Both `a` and `b` rows are present so the UI can show before/after side
 *  by side. For onlyInA / onlyInB only the present side is filled. */
const ScanDiffEntrySchema = z.object({
  pointer: z.string(),
  a:       ComplianceResultSchema.optional(),
  b:       ComplianceResultSchema.optional(),
});
export type ScanDiffEntry = z.infer<typeof ScanDiffEntrySchema>;

export const ComplianceScanDiffSchema = z.object({
  a:          ScanDiffHeadSchema,
  b:          ScanDiffHeadSchema,
  warnings:   z.array(z.string()).default([]),
  fixed:      z.array(ScanDiffEntrySchema),
  regressed:  z.array(ScanDiffEntrySchema),
  persistent: z.array(ScanDiffEntrySchema),
  onlyInA:    z.array(ScanDiffEntrySchema),
  onlyInB:    z.array(ScanDiffEntrySchema),
});
export type ComplianceScanDiff = z.infer<typeof ComplianceScanDiffSchema>;
